import { type Locale, DEFAULT_LOCALE } from './index';

/** Split a path into its pathname and the trailing `?query#hash` (kept verbatim). */
function splitSuffix(path: string): [string, string] {
  const i = path.search(/[?#]/);
  return i === -1 ? [path, ''] : [path.slice(0, i), path.slice(i)];
}

/**
 * Remove a leading locale segment (`/ru`, `/uz` or `/kk`) from a pathname, if
 * present. `/ru/tender/1` -> `/tender/1`; `/ru` -> `/`; `/rubles` -> `/rubles`
 * (no match — the alternation is anchored to a full segment).
 */
export function stripLocalePrefix(path: string): string {
  const [pathname, suffix] = splitSuffix(path);
  const m = pathname.match(/^\/(ru|uz|kk)(\/.*|)$/);
  if (!m) return path;
  const rest = m[2] === '' ? '/' : m[2];
  return `${rest}${suffix}`;
}

/**
 * Prefix a bare in-app path with the active locale's segment. The DEFAULT locale
 * is unprefixed (bare); every other locale gets its own `/{locale}` segment.
 * Idempotent — a path already carrying a locale prefix is first stripped, so
 * re-prefixing never doubles it. With `DEFAULT_LOCALE='ru'`:
 * `localePath('/tender/1','uz')` -> `/uz/tender/1`; `localePath('/','uz')` -> `/uz`;
 * `localePath('/tender/1','ru')` -> `/tender/1` (bare).
 */
export function localePath(path: string, locale: Locale): string {
  const bare = stripLocalePrefix(path);
  if (locale === DEFAULT_LOCALE) return bare;
  const [pathname, suffix] = splitSuffix(bare);
  const trimmed = pathname === '/' ? '' : pathname;
  return `/${locale}${trimmed}${suffix}`;
}

/** Swap the locale of a full current path (used by the language switcher). */
export function toggleLocalePath(path: string, target: Locale): string {
  return localePath(path, target);
}
